import {
  createResource,
  deleteResource,
  determinePaginationParams,
  determineSelector,
  getResource,
  readUrl,
  throwOnApiError,
  updateResource,
  updateResourceBodyless,
  updateResourcePath,
} from "./api";

// The rows a company's PUBLIC page may show: not company-hidden, and either current (no end date)
// or company-highlighted — highlighting is the company's explicit "keep this past employee on our
// page" override (Hide and Highlight are mutually exclusive in the manage modal, so the two halves
// can't contradict). Opt-in via the `companyPageVisible` option so the manage modal's unfiltered
// reads keep seeing every row, including the ones this filter withholds from visitors.
const COMPANY_PAGE_VISIBLE_SELECTOR = "company_hidden=is.false&or=(ended_at.is.null,company_highlighted.is.true)";

const getAllByCompany = async (companyId, options = { page: 1, per_page: 10 }) => {
  let selector = determineSelector(companyId, { keyId: "company_id", keySlug: "company_slug" });
  const paginationParams = determinePaginationParams(options);

  if (options.companyPageVisible) {
    selector = `${selector}&${COMPANY_PAGE_VISIBLE_SELECTOR}`;
  }

  const { highlighted } = options;
  if (highlighted === true) {
    selector = `${selector}&company_highlighted=eq.true`;
  } else if (highlighted === false) {
    selector = `${selector}&company_highlighted=eq.false`;
  }

  const { hidden } = options;
  if (hidden === true) {
    selector = `${selector}&company_hidden=eq.true`;
  } else if (hidden === false) {
    selector = `${selector}&company_hidden=eq.false`;
  }

  const url = readUrl("company_jobs", { selector, paginationParams });
  return getResource(url);
};

const getAllProfilesByCompany = async (companyId, options = { page: 1, per_page: 10 }) => {
  let selector = determineSelector(companyId, { keyId: "company_id", keySlug: "company_slug" });
  const personType = "Id::Profile::Promoter";
  selector = `${selector}&person_type=eq.${personType}`;
  if (options.companyPageVisible) {
    selector = `${selector}&${COMPANY_PAGE_VISIBLE_SELECTOR}`;
  }
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("company_jobs", { selector, paginationParams });
  return getResource(url);
};

const EXPERT_TYPE = "Id::PromoEntity::Expert";
const PROMOTER_TYPE = "Id::Profile::Promoter";
export const TEAM_ROSTER_PAGE_SIZE = 100;

// The "others at this company" roster behind the Team Members card on PR and expert profiles: the
// company's CURRENT experts and PR people, minus the profile being viewed (and, for a self-representing
// expert, their own PR profile). Person ids are only unique per type, so exclusions are (type, id) pairs.
//
// One page of up to 100 rows, deliberately: the card shows a handful of avatars plus a "+N" chip, and
// the caller counts what lies beyond this page from `totalRows`. Rows are jobs, not people — nothing
// makes (person, company) unique, so one colleague can appear on several rows and the caller must dedupe.
const getTeamProfilesByCompany = async (companyId, { excludePromoterId, excludeExpertId, expertsFirst } = {}) => {
  let selector = determineSelector(companyId, { keyId: "company_id", keySlug: "company_slug" });
  selector = `${selector}&person_type=in.(${EXPERT_TYPE},${PROMOTER_TYPE})`;
  // company_hidden = "don't list me on the company's profile", and this IS a company-team display —
  // just hosted on a colleague's page — so honor it. person_hidden needs no selector: RLS on
  // id_company_jobs already withholds those rows from everyone but the person themself and admins.
  selector = `${selector}&company_hidden=is.false&ended_at=is.null`;
  const exclusions = [
    excludePromoterId && `not.and(person_type.eq.${PROMOTER_TYPE},person_id.eq.${excludePromoterId})`,
    excludeExpertId && `not.and(person_type.eq.${EXPERT_TYPE},person_id.eq.${excludeExpertId})`,
  ].filter(Boolean);
  if (exclusions.length) {
    selector = `${selector}&and=(${exclusions.join(",")})`;
  }
  // The viewed page's own kind first ("Id::Profile::…" sorts before "Id::PromoEntity::…"), then
  // company-curated, then holders of their primary employment, then a stable key so the avatars the
  // caller slices off the top don't reshuffle between reads.
  const ownKindFirst = expertsFirst ? "person_type.desc" : "person_type.asc";
  selector = `${selector}&order=${ownKindFirst},company_highlighted.desc,person_featured.desc,person_id.asc,id.asc`;
  const paginationParams = determinePaginationParams({ page: 1, per_page: TEAM_ROSTER_PAGE_SIZE });
  const url = readUrl("company_jobs", { selector, paginationParams });
  return getResource(url);
};

// The Company page's own "People" rail card: this company's CURRENT, non-company-hidden Promoter
// roster, paged through to a provable total rather than capped at one page. The PR half of
// getTeamProfilesByCompany's filters (current, visible employment only), parameterized for pagination
// instead of a fixed per_page:100 cap. Rows are jobs, not people; the caller dedupes by person_id once
// every page has arrived.
//
// `person_id` alone is not unique — one person can hold more than one current job at the company, so
// rows tie on it — and PostgREST does not guarantee tie order is stable across requests with different
// offsets. Without a unique tiebreaker, rows on either side of a tied boundary can be skipped or
// duplicated between pages. `id` (the company_job row's own id) is the view's only per-row-unique
// column, so it closes out the order as the final key.
const getAllTeamProfilesByCompany = async (companyId, options = { page: 1, per_page: 100 }) => {
  let selector = determineSelector(companyId, { keyId: "company_id", keySlug: "company_slug" });
  const personType = "Id::Profile::Promoter";
  selector = `${selector}&person_type=eq.${personType}`;
  selector = `${selector}&company_hidden=is.false&ended_at=is.null`;
  selector = `${selector}&order=company_highlighted.desc,person_featured.desc,person_id.asc,id.asc`;
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("company_jobs", { selector, paginationParams });
  return getResource(url);
};

const get = async (companyJobId) => {
  const selector = determineSelector(companyJobId, { keyId: "id" });
  const url = readUrl("company_jobs", { selector });
  return getResource(url);
};

const updateByCompanyIdAndId = async (companyId, id, attributes) => {
  const url = updateResourcePath(`companies/${companyId}/jobs`, id);
  return updateResource(url, { data: { attributes } });
};

const updateByPersonIdAndId = async (personId, id, attributes) => {
  const url = `/id/api/user_profiles/${personId}/pr_jobs/${id}`;
  return throwOnApiError(await updateResource(url, { data: { attributes } }));
};

// Fetches ALL of a PR profile's employment (no pagination), ordered featured/current-first. This backs
// BOTH the show-page Employment section and the edit modal (the provider owns the collection), so it must
// return the complete, ordered set — a truncated page or a client-side re-sort of one page would corrupt
// both. Order matches the old header query (person_featured desc, then most-recent started_at first),
// with `id` as a TOTAL final key: `started_at` is optional, so tied or null dates would otherwise let
// jobs swap places between reads — and consumers that read "the first job" (the header's company, the
// Team Members roster) would follow them around.
const getAllByPrProfile = async (prProfileId) => {
  const selector = determineSelector(prProfileId, { keyId: "person_id", keySlug: "person_slug" });
  const personType = "Id::Profile::Promoter";
  const url = readUrl("company_jobs", {
    selector: `${selector}&person_type=eq.${personType}&order=person_featured.desc,started_at.desc,id.asc`,
  });
  return getResource(url);
};

// Same contract for an expert's employment (person is the PromoEntity, not the profile).
const getAllByExpert = async (expertId) => {
  const selector = determineSelector(expertId, { keyId: "person_id", keySlug: "person_slug" });
  const personType = "Id::PromoEntity::Expert";
  const url = readUrl("company_jobs", {
    selector: `${selector}&person_type=eq.${personType}&order=person_featured.desc,started_at.desc`,
  });
  return getResource(url);
};

const createPrProfileJob = async (companyId, personIdOrSlug, attributes) => {
  const url = `/id/api/user_profiles/${personIdOrSlug}/pr_jobs`;
  // Check for `{ error }` BEFORE unwrapping to attributes — otherwise a failed create returns
  // `undefined` and the caller can't tell success from failure.
  const response = throwOnApiError(await createResource(url, { data: { attributes } }));
  return response?.data?.data?.attributes;
};

const deletePrProfileJobById = async (personIdOrSlug, id) => {
  const url = `/id/api/user_profiles/${personIdOrSlug}/pr_jobs/${id}`;
  return throwOnApiError(await deleteResource(url));
};

// The expert-side trio of the three PR-profile job mutations above. Same controller and same payload
// shape — the person is the Expert promo entity rather than the promoter profile, so the path is
// `experts/:id/jobs` instead of `user_profiles/:id/pr_jobs`. throwOnApiError is applied here (not at the
// call site) because these methods have a single consumer that expects a thrown failure.
const createExpertJob = async (expertIdOrSlug, attributes) => {
  const url = `/id/api/experts/${expertIdOrSlug}/jobs`;
  // Check for `{ error }` BEFORE unwrapping to attributes — otherwise a failed create returns
  // `undefined` and the caller can't tell success from failure.
  const response = throwOnApiError(await createResource(url, { data: { attributes } }));
  return response?.data?.data?.attributes;
};

const updateExpertJobById = async (expertIdOrSlug, id, attributes) => {
  const url = `/id/api/experts/${expertIdOrSlug}/jobs/${id}`;
  return throwOnApiError(await updateResource(url, { data: { attributes } }));
};

const deleteExpertJobById = async (expertIdOrSlug, id) => {
  const url = `/id/api/experts/${expertIdOrSlug}/jobs/${id}`;
  return throwOnApiError(await deleteResource(url));
};

// feature/unfeature are a bodyless PUT pair (FlippableRoute), not a single toggle endpoint — the
// caller's `featured` boolean selects which of the two routes to call, same shape as quotes'. The
// server enforces one featured job per person: featuring a job unfeatures its siblings, so callers
// should reload the collection rather than patch it locally.
const setExpertJobFeaturedById = async (expertIdOrSlug, id, featured) => {
  const action = featured ? "feature" : "unfeature";
  const url = `/id/api/experts/${expertIdOrSlug}/jobs/${id}/${action}`;
  return throwOnApiError(await updateResourceBodyless(url));
};

const setPrProfileJobFeaturedById = async (personIdOrSlug, id, featured) => {
  const action = featured ? "feature" : "unfeature";
  const url = `/id/api/user_profiles/${personIdOrSlug}/pr_jobs/${id}/${action}`;
  return throwOnApiError(await updateResourceBodyless(url));
};

export default {
  createExpertJob,
  createPrProfileJob,
  deleteExpertJobById,
  deletePrProfileJobById,
  get,
  getAllByCompany,
  getAllByExpert,
  getAllByPrProfile,
  getAllProfilesByCompany,
  getAllTeamProfilesByCompany,
  getTeamProfilesByCompany,
  setExpertJobFeaturedById,
  setPrProfileJobFeaturedById,
  updateByCompanyIdAndId,
  updateByPersonIdAndId,
  updateExpertJobById,
};
